import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  AttachMoney,
  CardGiftcard,
  Business,
  Assessment,
  Timeline,
  PieChart,
  BarChart,
} from '@mui/icons-material';
import api from '../services/api';

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('month');
  const [analyticsData, setAnalyticsData] = useState<any>({
    revenue: { total: 0, growth: 0, chart: [] },
    users: { total: 0, active: 0, new: 0, chart: [] },
    benefits: { total: 0, redeemed: 0, popular: [] },
    agents: { total: 0, active: 0, performance: [] },
  });

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching analytics...');
      const response = await api.get(`/admin/analytics?period=${period}`);
      setAnalyticsData(response.data);
      console.log('Analytics loaded successfully');
    } catch (err: any) {
      console.log('Analytics API failed (non-critical):', err.message);
      setError('Analytics data unavailable - showing default view');
      // Don't crash - just show empty data with proper structure
      setAnalyticsData({
        overview: { totalUsers: 0, activeMembers: 0, revenue: 0, redemptions: 0 },
        revenue: { total: 0, growth: 0 },
        users: { total: 0, growth: 0, active: 0, new: 0 },
        redemptions: { total: 0, growth: 0, pending: 0, redeemed: 0 },
        benefits: { total: 0, active: 0, popular: [], redeemed: 0 },
        agents: { total: 0, active: 0, performance: [] },
        chartData: [],
        topBenefits: [],
        userGrowth: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `R${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `R${(amount / 1000).toFixed(1)}K`;
    }
    return `R${amount}`;
  };

  const getGrowthColor = (growth: number) => {
    return growth >= 0 ? '#4CAF50' : '#F44336';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#FFD700' }} />
      </Box>
    );
  }

  // If no analytics data, show a simple error message instead of crashing
  if (!analyticsData || !analyticsData.revenue || !analyticsData.agents) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
          Analytics
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Analytics data is not available. The API endpoint may not be implemented yet.
        </Alert>
        <Typography variant="body1" sx={{ color: '#888' }}>
          This page will show detailed analytics once the backend API is ready.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" sx={{ color: '#888' }}>
            Comprehensive platform insights and metrics
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel sx={{ color: '#888' }}>Period</InputLabel>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            label="Period"
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
            }}
          >
            <MenuItem value="week">Last 7 Days</MenuItem>
            <MenuItem value="month">Last 30 Days</MenuItem>
            <MenuItem value="quarter">Last 3 Months</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#FFD700', width: 48, height: 48 }}>
                  <AttachMoney />
                </Avatar>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {(analyticsData?.revenue?.growth || 0) >= 0 ? <TrendingUp /> : <TrendingDown />}
                  <Typography
                    variant="body2"
                    sx={{ color: getGrowthColor(analyticsData?.revenue?.growth || 0) }}
                  >
                    {Math.abs(analyticsData?.revenue?.growth || 0)}%
                  </Typography>
                </Box>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                {formatCurrency(analyticsData?.revenue?.total || 0)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                Total Revenue
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#4ECDC4', width: 48, height: 48 }}>
                  <People />
                </Avatar>
                <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                  +{analyticsData.users.new}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                {analyticsData.users.total.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                Total Users ({analyticsData.users.active} active)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#FF6B6B', width: 48, height: 48 }}>
                  <CardGiftcard />
                </Avatar>
                <Typography variant="body2" sx={{ color: '#888' }}>
                  {((analyticsData.benefits.redeemed / analyticsData.benefits.total) * 100).toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                {analyticsData.benefits.redeemed}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                Benefits Redeemed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#9575CD', width: 48, height: 48 }}>
                  <Business />
                </Avatar>
                <Typography variant="body2" sx={{ color: '#888' }}>
                  {((analyticsData.agents.active / analyticsData.agents.total) * 100).toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                {analyticsData.agents.active}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                Active Agents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Trend */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Timeline sx={{ color: '#FFD700' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Revenue Trend
                </Typography>
              </Box>
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255, 215, 0, 0.1)', borderRadius: 2 }}>
                <Typography variant="body1" sx={{ color: '#888' }}>
                  Revenue chart visualization
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Popular Benefits */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <PieChart sx={{ color: '#FFD700' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Popular Benefits
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {analyticsData.benefits.popular.slice(0, 5).map((benefit: any, index: number) => (
                  <Box key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {benefit.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888' }}>
                        {benefit.count} uses
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(benefit.count / analyticsData.benefits.popular[0].count) * 100}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#FFD700',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Agent Performance Table */}
      <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <BarChart sx={{ color: '#FFD700' }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
              Top Performing Agents
            </Typography>
          </Box>
          <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Rank
                  </TableCell>
                  <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Agent Name
                  </TableCell>
                  <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Sign-ups
                  </TableCell>
                  <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Revenue Generated
                  </TableCell>
                  <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Commission Earned
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsData.agents.performance.slice(0, 10).map((agent: any, index: number) => (
                  <TableRow key={agent.id}>
                    <TableCell sx={{ color: '#FFD700', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      #{index + 1}
                    </TableCell>
                    <TableCell sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      {agent.name}
                    </TableCell>
                    <TableCell sx={{ color: '#CCC', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      {agent.signups}
                    </TableCell>
                    <TableCell sx={{ color: '#CCC', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      {formatCurrency(agent.revenue)}
                    </TableCell>
                    <TableCell sx={{ color: '#4CAF50', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      {formatCurrency(agent.commission)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Analytics;